import React, { useState, useEffect, useCallback } from 'react';
import { supabase, OperationType, handleSupabaseError } from '../supabase';
import {
  AlertCircle,
  ShieldCheck,
  User as UserIcon,
  CheckCircle2,
  Loader2,
  Check,
  LogIn,
} from 'lucide-react';
import { AuthProvider } from '../types';
import { debounce } from 'lodash';
import type { User as SupabaseUser } from '@supabase/supabase-js';

const inputBase =
  'w-full h-12 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-sm font-medium text-[#0b0b0c] placeholder:text-[#0b0b0c]/40 focus:outline-none focus:ring-2 focus:ring-[#9945ff]/30';

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-1.5 mt-3 font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
    {children}
  </div>
);

interface PendingNewUser {
  user: SupabaseUser;
  provider: AuthProvider;
  twitterHandle?: string;
}

export const Auth: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nicknameLoading, setNicknameLoading] = useState(false);
  const [nicknameStatus, setNicknameStatus] = useState<'available' | 'taken' | 'none'>('none');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [pendingUser, setPendingUser] = useState<PendingNewUser | null>(null);
  const [nickname, setNickname] = useState('');
  const [companyName, setCompanyName] = useState('');

  // After OAuth redirect, Supabase puts a session in the URL hash; the listener
  // in App.tsx picks that up. Here we only watch for "user signed in but no profile".
  useEffect(() => {
    const checkPendingProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();
      if (!profile) {
        const provider = (user.app_metadata?.provider === 'twitter' ? 'twitter' : 'google') as AuthProvider;
        const twitterHandle =
          (user.user_metadata?.user_name as string | undefined) ||
          (user.user_metadata?.preferred_username as string | undefined) ||
          undefined;
        setPendingUser({ user, provider, twitterHandle });
      }
    };
    void checkPendingProfile();
    const { data: sub } = supabase.auth.onAuthStateChange(() => void checkPendingProfile());
    return () => sub.subscription.unsubscribe();
  }, []);

  const checkUniqueness = async (name: string) => {
    if (!name || name.length < 2) {
      setNicknameStatus('none');
      setSuggestions([]);
      return;
    }
    setNicknameLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('profiles')
        .select('id')
        .ilike('display_name', name)
        .limit(1);
      if (err) throw err;
      if (!data || data.length === 0) {
        setNicknameStatus('available');
        setSuggestions([]);
      } else {
        setNicknameStatus('taken');
        const bases = [
          `${name}${Math.floor(Math.random() * 99)}`,
          `${name}_${Math.floor(Math.random() * 99)}`,
          `${name}2026`,
          `${name}_v2`,
          `the_${name}`,
        ];
        setSuggestions(bases.sort(() => 0.5 - Math.random()).slice(0, 3));
      }
    } catch (err) {
      console.error('Error checking uniqueness:', err);
    } finally {
      setNicknameLoading(false);
    }
  };

  const debouncedCheck = useCallback(
    debounce((name: string) => checkUniqueness(name), 500),
    [],
  );

  useEffect(() => {
    if (nickname) {
      setNicknameStatus('none');
      debouncedCheck(nickname);
    } else {
      setNicknameStatus('none');
    }
  }, [nickname, debouncedCheck]);

  const handleSignIn = async (providerType: AuthProvider) => {
    setLoading(true);
    setError(null);
    try {
      const { error: err } = await supabase.auth.signInWithOAuth({
        provider: providerType,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: providerType === 'google'
            ? { access_type: 'offline', prompt: 'consent' }
            : undefined,
        },
      });
      if (err) throw err;
      // Browser is redirecting; loading stays true until back on page.
    } catch (err: any) {
      setError(err?.message ?? `Login failed (${providerType})`);
      setLoading(false);
    }
  };

  const handleSubmitProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingUser || !nickname || !companyName) return;
    setLoading(true);
    setError(null);
    try {
      const { error: err } = await supabase.from('profiles').insert({
        id: pendingUser.user.id,
        display_name: nickname,
        company_name: companyName,
        email: pendingUser.user.email ?? null,
        twitter_handle: pendingUser.twitterHandle ?? null,
        auth_provider: pendingUser.provider,
        role: 'client',
        tier: 'standard',
        is_approved: false,
      });
      if (err) throw err;
      setPendingUser(null);
      // App.tsx subscribes to profile changes and will pick up the new row.
    } catch (err: any) {
      console.error('Profile creation error:', err);
      setError(err?.message ?? 'Failed to create profile. Please try again.');
      handleSupabaseError(err, OperationType.INSERT, 'profiles');
    } finally {
      setLoading(false);
    }
  };

  if (pendingUser) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <div className="sticker bg-[#e9dffa] w-full max-w-md p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 sticker-soft bg-[#0b0b0c] inline-flex items-center justify-center mx-auto mb-4">
              <UserIcon className="w-7 h-7 text-white" />
            </div>
            <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
              One more step
            </p>
            <h2 className="font-bold text-3xl uppercase tracking-tight text-[#0b0b0c] mt-1">
              Complete your profile
            </h2>
          </div>

          <div className="sticker-soft bg-white p-3 mb-5">
            <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55 mb-0.5">
              Authenticated as
            </p>
            <p className="text-sm font-bold text-[#0b0b0c] truncate">
              {pendingUser.user.email || `@${pendingUser.twitterHandle}`}
            </p>
          </div>

          <form onSubmit={handleSubmitProfile} className="space-y-1">
            <FieldLabel>Nickname</FieldLabel>
            <div className="relative">
              <input
                required
                type="text"
                placeholder="e.g. solbuilder"
                className={`${inputBase} px-5 pr-12 ${
                  nicknameStatus === 'taken'
                    ? 'border-[#ff6a6a]'
                    : nicknameStatus === 'available'
                    ? 'border-[#14f195]'
                    : ''
                }`}
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {nicknameLoading ? (
                  <Loader2 className="w-4 h-4 text-[#0b0b0c]/45 animate-spin" />
                ) : nicknameStatus === 'available' ? (
                  <Check className="w-4 h-4 text-[#14f195]" />
                ) : nicknameStatus === 'taken' ? (
                  <AlertCircle className="w-4 h-4 text-[#ff6a6a]" />
                ) : null}
              </div>
            </div>
            {nicknameStatus === 'taken' && (
              <div className="space-y-1.5 mt-1.5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#ff6a6a]">
                  Nickname taken
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setNickname(s)}
                      className="px-3 min-h-9 rounded-full bg-white border-[1.5px] border-[#0b0b0c] text-[10px] font-bold hover:bg-[#0b0b0c] hover:text-white transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <FieldLabel>Company name</FieldLabel>
            <input
              required
              type="text"
              placeholder="e.g. Phantom Wallet"
              className={`${inputBase} px-5`}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />

            {error && (
              <div className="sticker-soft bg-[#ff6a6a] text-white mt-3 p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={
                loading || !nickname || !companyName || nicknameStatus === 'taken' || nicknameLoading
              }
              className="w-full h-12 mt-4 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Submit profile
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4">
      <div className="sticker bg-[#e9dffa] w-full max-w-md p-6 sm:p-8 text-center">
        <div className="w-14 h-14 sticker-soft bg-[#0b0b0c] inline-flex items-center justify-center mx-auto mb-4">
          <LogIn className="w-7 h-7 text-white" />
        </div>
        <h2 className="font-bold text-4xl uppercase tracking-tight text-[#0b0b0c] mb-1">
          Submit an event
        </h2>
        <p className="text-sm font-medium text-[#0b0b0c]/65 mb-6">
          Sign in to access the calendar
        </p>

        {error && (
          <div className="sticker-soft bg-[#ff6a6a] text-white mb-5 p-3 flex items-start gap-2 text-left">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => handleSignIn('google')}
            disabled={loading}
            className="w-full h-12 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm hover:bg-[#0b0b0c] hover:text-white disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2.5"
          >
            <img src="https://www.google.com/favicon.ico" className="w-4 h-4" alt="Google" />
            {loading ? 'Processing...' : 'Continue with Google'}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="h-px bg-[#0b0b0c]/15 flex-1" />
            <span className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/45">
              or
            </span>
            <div className="h-px bg-[#0b0b0c]/15 flex-1" />
          </div>

          <button
            onClick={() => handleSignIn('twitter')}
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm hover:bg-[#5b29a6] disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2.5"
          >
            <span className="text-lg leading-none font-bold">𝕏</span>
            {loading ? 'Processing...' : 'Continue with X'}
          </button>
        </div>
      </div>

      <div className="mt-4 inline-flex items-center gap-1.5 text-[#0b0b0c]/55 text-[11px] font-medium">
        <ShieldCheck className="w-3.5 h-3.5" />
        All submissions are subject to admin approval
      </div>
    </div>
  );
};
